import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckIcon from '@mui/icons-material/Check';
import { selectIsFavourite, toggleFavourite } from '../store/favouritesSlice';
import { addOrUpdateItem, selectCartEntry } from '../store/cartSlice';
import {
  formatProductOriginalPrice,
  formatProductPrice,
  hasProductDiscount,
  hasProductPrice,
} from '../utils/price';
import { isProductActive } from '../utils/mapProduct';
import { buildProductConsultWhatsAppMessage, openWhatsApp } from '../utils/whatsapp';
import { CardImage } from './CardImage';
import { useCartMessage } from '../context/CartMessageContext';

export default function ProductGridItem({
  item,
  addToCartLabel,
  favouriteLabel,
  discountOffLabel,
  unavailableLabel,
  vipLabel,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { showCartMessage } = useCartMessage();
  const isFavourite = useSelector(selectIsFavourite(item.id));
  const cartEntry = useSelector(selectCartEntry(item.id));
  const [cartJustAdded, setCartJustAdded] = useState(false);

  const isActive = isProductActive(item);
  const isVip = Boolean(item.isVip);
  const hidePrice = Boolean(item.hidePrice);
  const hasDiscount = !hidePrice && hasProductDiscount(item);
  const showPrice = !hidePrice && hasProductPrice(item);

  const openProduct = useCallback(() => {
    navigate(`/product/${item.id}`, { state: { product: item } });
  }, [navigate, item]);

  const quickAddToCart = useCallback(
    (e) => {
      e.stopPropagation();
      if (!isActive || hidePrice) return;
      dispatch(
        addOrUpdateItem({
          product: item,
          quantity: (cartEntry?.quantity ?? 0) + 1,
          note: cartEntry?.note ?? '',
        }),
      );
      setCartJustAdded(true);
      setTimeout(() => setCartJustAdded(false), 750);
      showCartMessage(t('added_to_cart'), 'success');
    },
    [cartEntry, dispatch, hidePrice, isActive, item, showCartMessage, t],
  );

  const openProductConsult = useCallback(
    (e) => {
      e.stopPropagation();
      const message = buildProductConsultWhatsAppMessage({
        product: item,
        currency: t('orders_currency'),
        labels: {
          title: t('product_wa_consult_title'),
          product: t('product_wa_product'),
          price: t('product_wa_price'),
          originalPrice: t('product_wa_original_price'),
          discount: t('product_wa_discount'),
        },
      });
      openWhatsApp(message);
    },
    [item, t],
  );

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        transition: 'all 0.22s ease',
        opacity: isActive ? 1 : 0.78,
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 12px 32px rgba(42,147,143,0.16)',
          borderColor: 'primary.light',
        },
      }}
    >
      <CardActionArea onClick={openProduct} sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            position: 'relative',
            p: { xs: 1.25, sm: 1.5 },
            background:
              'linear-gradient(180deg, rgba(58,181,176,0.1) 0%, rgba(42,147,143,0.04) 100%)',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <CardImage
            src={item.image}
            alt={item.name}
            aspectRatio="1 / 1"
            rounded={3}
            iconSize={40}
            fit="contain"
            inactive={!isActive}
            bordered={false}
          />

          {!isActive && (
            <Chip
              label={unavailableLabel}
              size="small"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: 'rgba(0,0,0,0.7)',
                color: '#fff',
                fontWeight: 700,
                zIndex: 2,
              }}
            />
          )}

          {isVip && (
            <Chip
              label={vipLabel || 'VIP'}
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                insetInlineStart: 12,
                fontWeight: 800,
                fontSize: 11,
                zIndex: 2,
                background: 'linear-gradient(135deg, #D4A574, #B8894A)',
                color: '#fff',
              }}
            />
          )}

          {hasDiscount && item.discountPercent != null && (
            <Chip
              label={`${item.discountPercent}% ${discountOffLabel}`}
              size="small"
              color="secondary"
              sx={{
                position: 'absolute',
                top: isVip ? 44 : 12,
                insetInlineStart: 12,
                fontWeight: 800,
                fontSize: 11,
                zIndex: 2,
              }}
            />
          )}

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              dispatch(toggleFavourite(item.id));
            }}
            aria-label={favouriteLabel}
            sx={{
              position: 'absolute',
              top: 10,
              insetInlineEnd: 10,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              boxShadow: 1,
              zIndex: 2,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            {isFavourite ? (
              <FavoriteIcon color="secondary" fontSize="small" />
            ) : (
              <FavoriteBorderIcon color="primary" fontSize="small" />
            )}
          </IconButton>
        </Box>

        <CardContent sx={{ pt: 1.5, pb: 1, px: { xs: 1.5, sm: 2 } }}>
          <Typography
            variant="body2"
            fontWeight={700}
            align="center"
            sx={{
              minHeight: 40,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.45,
            }}
          >
            {item.name}
          </Typography>
        </CardContent>
      </CardActionArea>

      <CardActions
        sx={{
          px: { xs: 1.5, sm: 2 },
          pb: { xs: 1.5, sm: 2 },
          pt: 0,
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 'auto',
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <IconButton
            onClick={quickAddToCart}
            disabled={!isActive || hidePrice}
            aria-label={addToCartLabel}
            color={cartJustAdded ? 'success' : 'primary'}
            sx={{
              bgcolor: cartJustAdded ? 'success.main' : 'primary.main',
              color: '#fff',
              '&:hover': { bgcolor: cartJustAdded ? 'success.dark' : 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
            }}
          >
            {cartJustAdded ? <CheckIcon fontSize="small" /> : <ShoppingCartOutlinedIcon fontSize="small" />}
          </IconButton>
          <IconButton
            onClick={openProductConsult}
            aria-label={t('product_consult_whatsapp')}
            sx={{
              bgcolor: '#25D366',
              color: '#fff',
              '&:hover': { bgcolor: '#1da851' },
            }}
          >
            <WhatsAppIcon fontSize="small" />
          </IconButton>
        </Box>

        {hidePrice ? (
          <Box
            sx={{
              maxWidth: { xs: 118, sm: 132 },
              px: 1,
              py: 0.65,
              borderRadius: 2,
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(212,165,116,0.18), rgba(184,137,74,0.12))',
              border: '1px solid rgba(184,137,74,0.35)',
              boxShadow: '0 2px 8px rgba(184,137,74,0.12)',
            }}
          >
            <Typography
              component="span"
              sx={{
                display: 'block',
                fontWeight: 800,
                lineHeight: 1.35,
                fontSize: { xs: '0.68rem', sm: '0.74rem' },
                color: '#8F6A33',
                letterSpacing: 0.1,
              }}
            >
              {t('price_ask_message')}
            </Typography>
          </Box>
        ) : showPrice ? (
          <Box sx={{ textAlign: 'end' }}>
            <Typography variant="subtitle1" fontWeight={800} color="primary.main" lineHeight={1.2}>
              {formatProductPrice(item)}
            </Typography>
            {hasDiscount && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textDecoration: 'line-through' }}
              >
                {formatProductOriginalPrice(item)}
              </Typography>
            )}
          </Box>
        ) : null}
      </CardActions>
    </Card>
  );
}
